/**
 * AuthService — BEMS 세션 인증 관리
 * ====================================
 * - POST /api/auth/login  로 로그인 → sessionStorage에 토큰 저장
 * - GET  /api/auth/me     로 세션 검증 (앱 시작 시 1회)
 * - POST /api/auth/logout 로 로그아웃 → 토큰 폐기
 *
 * 보안 고려사항:
 * - sessionStorage 사용 (탭/브라우저 종료 시 자동 만료, localStorage보다 안전)
 * - 서버 측 8시간 세션 TTL + 브루트포스 방지 (5회 실패 → 15분 잠금)
 * - timingSafeEqual 비교로 타이밍 공격 방지 (서버 측)
 */

const TOKEN_KEY = 'bems_session_token';
const USER_KEY  = 'bems_session_user';

export interface AuthUser {
  username:  string;
  role:      'admin' | 'operator';
  name:      string;
  expiresAt?: number;
}

export interface LoginResult {
  success: boolean;
  error?:  string;
  user?:   AuthUser;
}

export class AuthService {
  // ── 토큰 / 유저 접근 ────────────────────────────────────────────────────────
  static getToken(): string | null {
    return sessionStorage.getItem(TOKEN_KEY);
  }

  static getUser(): AuthUser | null {
    const raw = sessionStorage.getItem(USER_KEY);
    if (!raw) return null;
    try { return JSON.parse(raw) as AuthUser; } catch { return null; }
  }

  static isAuthenticated(): boolean {
    return !!this.getToken();
  }

  // ── 로그인 ──────────────────────────────────────────────────────────────────
  static async login(username: string, password: string): Promise<LoginResult> {
    try {
      const res  = await fetch('/api/auth/login', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ username, password }),
      });
      const data = await res.json();
      if (!res.ok) return { success: false, error: data.error ?? '로그인 실패' };

      sessionStorage.setItem(TOKEN_KEY, data.token);
      sessionStorage.setItem(USER_KEY,  JSON.stringify(data.user));
      return { success: true, user: data.user };
    } catch {
      return { success: false, error: '서버에 연결할 수 없습니다. 서버가 실행 중인지 확인하세요.' };
    }
  }

  // ── 로그아웃 ────────────────────────────────────────────────────────────────
  static async logout(): Promise<void> {
    const token = this.getToken();
    if (token) {
      try {
        await fetch('/api/auth/logout', {
          method:  'POST',
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch { /* 서버 미연결이어도 클라이언트 세션은 반드시 삭제 */ }
    }
    this.clearSession();
  }

  // ── 세션 검증 (앱 시작 시) ──────────────────────────────────────────────────
  static async validateSession(): Promise<AuthUser | null> {
    const token = this.getToken();
    if (!token) return null;
    try {
      const res = await fetch('/api/auth/me', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const user: AuthUser = await res.json();
        sessionStorage.setItem(USER_KEY, JSON.stringify(user));
        return user;
      }
    } catch { /* 네트워크 오류 → 세션 유지 (서버 일시 다운 대응) */ }
    // 401/403이면 세션 무효
    this.clearSession();
    return null;
  }

  // ── 내부 헬퍼 ───────────────────────────────────────────────────────────────
  static clearSession(): void {
    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(USER_KEY);
  }

  /** fetch 호출 시 Authorization 헤더를 자동 첨부 */
  static authHeaders(): HeadersInit {
    const token = this.getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }
}
